import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { formatDistanceToNow } from "date-fns";
import { MapPin, Building2, Briefcase } from "lucide-react";
import PublicHeader from "@/components/PublicHeader";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JobRow = ({ j }) => (
  <Link to={`/jobs/${j.id}`} className="paper-card bg-white p-5 block hover:-translate-y-0.5 transition-transform duration-200" data-testid={`public-job-${j.id}`}>
    <div className="flex items-start justify-between gap-3">
      <h2 className="font-serif-display text-lg text-[color:var(--ayur-ink)]">{j.title}</h2>
      {j.is_sponsored && <span className="pill">Featured</span>}
    </div>
    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[color:var(--ayur-ink-2)]">
      <span className="inline-flex items-center gap-1"><Building2 className="w-3.5 h-3.5" />{j.hospital}</span>
      <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{j.location}</span>
      <span className="inline-flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" />{j.employment_type}</span>
      {j.degree_required && <span>{j.degree_required}</span>}
      {j.specialty && <span>{j.specialty}</span>}
    </div>
    <p className="mt-3 text-sm text-[color:var(--ayur-ink-2)] line-clamp-2">{j.description}</p>
    <div className="mt-3 text-xs text-[color:var(--ayur-mute)]">{formatDistanceToNow(new Date(j.created_at), { addSuffix: true })}</div>
  </Link>
);

export default function PublicJobs() {
  const [jobs, setJobs] = useState(null);
  useEffect(() => { axios.get(`${API}/public/jobs`).then((r) => setJobs(r.data)).catch(() => setJobs([])); }, []);
  return (
    <div className="min-h-screen bg-[color:var(--ayur-cream)]" data-testid="public-jobs-page">
      <Seo title="Ayurveda & BAMS Jobs in India — MD/MS Ayurveda vacancies"
        description="Latest BAMS, MD (Ayurveda) and MS (Ayurveda) jobs across India — clinical, Panchakarma, teaching and research vacancies posted by hospitals, colleges and senior Vaidyas."
        path="/jobs" keywords="BAMS jobs, Ayurveda jobs, MD Ayurveda jobs, MS Ayurveda jobs, Panchakarma jobs, Ayurveda doctor vacancy, Ayurveda lecturer jobs" />
      <PublicHeader appPath="/app/jobs" />
      <main className="max-w-5xl mx-auto px-6 py-10">
        <h1 className="font-serif-display text-4xl sm:text-5xl text-[color:var(--ayur-emerald)]">Ayurveda & BAMS jobs</h1>
        <p className="mt-3 text-sm md:text-base text-[color:var(--ayur-ink-2)] max-w-2xl">Clinical, Panchakarma, teaching and research roles for BAMS, MD & MS Ayurveda scholars across India. Log in to apply.</p>
        {!jobs && <p className="mt-8 text-sm text-[color:var(--ayur-mute)]" data-testid="public-jobs-loading">Loading jobs…</p>}
        {jobs && !jobs.length && <p className="mt-8 text-sm text-[color:var(--ayur-mute)]" data-testid="public-jobs-empty">No open jobs right now — check back soon.</p>}
        <div className="mt-8 grid gap-4" data-testid="public-jobs-list">{jobs?.map((j) => <JobRow key={j.id} j={j} />)}</div>
      </main>
    </div>
  );
}
